import React, { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Table } from '@components/common';

import { useMutateDashboardAssetRentsExport } from '@hooks/dashboardPO/useMutateDashboardAssetRentsExport';
import useQueryDashboardRentsSummary from '@hooks/dashboardPO/useQueryDashboardRentsSummary';

import { dashboardPORequestsTable } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSearchValue, setSort } from '@slices/dashboardPORequestsTable';

import { ISortBy, ITableSearch, ITableSort } from '@interfaces/components/table';
import { IExportFileDTO } from '@interfaces/platform/dto/platformDashboardDTO';

import DashboardPOWhiteCard from '../DashboardPOWhiteCard';
import { getRequestsColumns } from './utils';

import styles from './DashboardPORequestsTable.module.scss';

const MAX_TABLE_HEIGHT = '578px';

interface IDashboardPORequestsTable {
	fromText: string;
	toText: string;
}

const DashboardPORequestsTable = ({ fromText, toText }: IDashboardPORequestsTable) => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const {
		sortDirection,
		sortFieldName,
		searchValue,
	} = useAppSelector(dashboardPORequestsTable);

	const handleSort = useCallback((sortBy: ISortBy[]) => {
		dispatch(
			setSort({
				sortDirection: !!sortBy[0]?.desc,
				sortFieldName: sortBy[0]?.id,
			}),
		);
	}, [dispatch]);

	const searchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const search: ITableSearch[] = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: 'Search by Organisation name',
	}], [searchValue, searchHandler]);

	const columns = useMemo(() => getRequestsColumns({ navigate }), [navigate]);

	const { dashboardRentsSummary, isLoadingDashboardRentsSummary } = useQueryDashboardRentsSummary({
		fromText,
		toText,
		searchValue,
		sortDirection,
		sortFieldName,
	});

	const { exportDashboardRentsSummary } = useMutateDashboardAssetRentsExport();

	const handleExportRentsSummary = useCallback(({ fileType }: IExportFileDTO) => {
		if (!fileType) return;

		exportDashboardRentsSummary({
			fileType,
			fromText,
			toText,
		});
	}, [exportDashboardRentsSummary, fromText, toText]);

	return (
		<DashboardPOWhiteCard
			handleExport={handleExportRentsSummary}
			isWithExport
			title='Details of the completed requests'
		>
			<Table
				columns={columns}
				customRightAction={(
					<div className={styles.dateWrapper}>
						<span>Date:</span>
						<span>{fromText} - {toText}</span>
					</div>
				)}
				data={dashboardRentsSummary}
				isLoading={isLoadingDashboardRentsSummary}
				isWithPagination
				isWithoutActions
				isWithoutCheckbox
				isWithoutDeleteAction
				isWithoutFilter
				maxHeight={MAX_TABLE_HEIGHT}
				search={search}
				searchThreshold={0}
				sort={sort}
				stickyLeftColumnIndexes={[]}
			/>
		</DashboardPOWhiteCard>
	);
};

export default memo(DashboardPORequestsTable);
